import { useEffect, useState, type FormEvent } from 'react'
import { Icon } from './Icon'

export type ModuleModalMode = 'create' | 'view' | 'edit'

type Props = {
  mode: ModuleModalMode
  moduleTitle: string
  featureTitle: string
  columns: string[]
  row?: string[]
  onClose: () => void
  onEdit: () => void
  onSave: (row: string[]) => void
  onDelete?: () => void
  externalError?: string
  saving?: boolean
}

function emptyRow(columns: string[]): string[] {
  return columns.map((column, index) => {
    if (index === columns.length - 1 || /status/i.test(column)) return 'Open'
    return ''
  })
}

const STATUS_OPTIONS = [
  'Open',
  'Ready',
  'In progress',
  'Complete',
  'Needs review',
  'Scheduled',
  'Clocked in',
  'Normal',
  'Review',
  'Planned',
  'Published',
  'Draft',
  'Pending',
  'Submitted',
  'Active',
  'New',
  'Warm',
  'Alert',
  'Due',
]

export function ModuleRecordModal({
  mode,
  moduleTitle,
  featureTitle,
  columns,
  row,
  onClose,
  onEdit,
  onSave,
  onDelete,
  externalError,
  saving = false,
}: Props) {
  const [values, setValues] = useState<string[]>(() => row ? [...row] : emptyRow(columns))
  const [error, setError] = useState('')

  useEffect(() => {
    setValues(row ? [...row] : emptyRow(columns))
    setError('')
  }, [mode, row, columns])

  const title =
    mode === 'create'
      ? `Add ${featureTitle}`
      : mode === 'edit'
        ? `Edit ${featureTitle}`
        : `${featureTitle} details`

  const update = (index: number, value: string) => {
    setValues((current) => current.map((item, itemIndex) => itemIndex === index ? value : item))
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!values[0]?.trim()) {
      setError(`${columns[0]} is required.`)
      return
    }
    onSave(values.map((value) => value.trim()))
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section
        className="modal-panel module-record-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-panel__head">
          <div className="module-modal-title">
            <span className="eyebrow">{moduleTitle}</span>
            <h2>{title}</h2>
          </div>
          <button type="button" className="icon-btn" title="Close" onClick={onClose}>
            <span aria-hidden="true">×</span>
          </button>
        </div>

        {mode === 'view' ? (
          <div className="modal-panel__body">
            <dl className="module-detail-grid">
              {columns.map((column, index) => (
                <div key={column}>
                  <dt>{column}</dt>
                  <dd>{row?.[index] || '—'}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : (
          <form id="module-record-form" className="modal-panel__body module-record-form" onSubmit={submit}>
            {columns.map((column, index) => (
              <label key={column}>
                <span>{column}{index === 0 ? <em>*</em> : null}</span>
                {index === columns.length - 1 || /status/i.test(column) ? (
                  <select
                    value={values[index] || 'Open'}
                    onChange={(event) => update(index, (event.target as HTMLSelectElement).value)}
                  >
                    {Array.from(new Set([values[index] || 'Open', ...STATUS_OPTIONS])).map((status) => (
                      <option key={status}>{status}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={/date|due|recorded/i.test(column) ? 'date' : /count|quantity/i.test(column) ? 'number' : 'text'}
                    min={/count|quantity/i.test(column) ? 0 : undefined}
                    step={/count|quantity/i.test(column) ? 1 : undefined}
                    value={values[index] || ''}
                    placeholder={`Enter ${column.toLowerCase()}`}
                    aria-label={column}
                    onInput={(event) => update(index, (event.target as HTMLInputElement).value)}
                  />
                )}
              </label>
            ))}
            {error || externalError ? <p className="form-error" role="alert">{error || externalError}</p> : null}
          </form>
        )}

        <div className="modal-panel__foot">
          {mode === 'edit' && onDelete ? (
            <button type="button" className="btn btn--danger btn--sm module-delete-btn" onClick={onDelete}>
              Delete
            </button>
          ) : null}
          <span className="module-modal-spacer" />
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            {mode === 'view' ? 'Close' : 'Cancel'}
          </button>
          {mode === 'view' ? (
            <button type="button" className="btn btn--primary btn--sm" onClick={onEdit}>
              <Icon name="edit" /> Edit
            </button>
          ) : (
            <button type="submit" form="module-record-form" className="btn btn--primary btn--sm" disabled={saving}>
              <Icon name="check" /> {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </section>
    </div>
  )
}

type ExportProps = {
  moduleTitle: string
  filename: string
  csv: string
  onClose: () => void
}

export function ModuleExportModal({ moduleTitle, filename, csv, onClose }: ExportProps) {
  const href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section
        className="modal-panel module-record-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Export ${moduleTitle}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-panel__head">
          <div className="module-modal-title">
            <span className="eyebrow">{moduleTitle}</span>
            <h2>Export CSV</h2>
          </div>
          <button type="button" className="icon-btn" title="Close" onClick={onClose}>
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="modal-panel__body">
          <label className="module-export-preview">
            <span>CSV preview</span>
            <textarea readOnly value={csv} aria-label="CSV preview" />
          </label>
          <p className="module-export-meta">
            {csv.split('\n').length - 1} records · {filename}
          </p>
        </div>
        <div className="modal-panel__foot">
          <span className="module-modal-spacer" />
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>Cancel</button>
          <a className="btn btn--primary btn--sm" href={href} download={filename}>
            <Icon name="reports" /> Download CSV
          </a>
        </div>
      </section>
    </div>
  )
}
